import type {
  CloneFlowRequest,
  CloneFlowResponse,
  FlowField,
  FlowStatus,
} from "@repo/shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "@/lib/api/_client";
import { setFlowStatus } from "@/lib/api/flow-editor";
import { CLONE_JOBS_KEY } from "@/lib/hooks/use-clone-jobs";
import type { Flow } from "@/lib/types/flow";

/** Refetch every flow query, for a flow written outside this tab's mutations (a background copy). */
export const invalidateFlows = (qc: QueryClient) => qc.invalidateQueries({ queryKey: ["flows"] });

export function useFlows() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["flows"],
    queryFn: () => get<Flow[]>("/internal/flows"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["flows"] });
  return { flows: data ?? [], error, isLoading, mutate };
}

export function useSetFlowStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: FlowStatus }) =>
      setFlowStatus(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["flows"] }),
  });
}

// QL-401 — the "Supported for followup" opt-in gate. The server validates the
// enable prerequisites (active + collection + goal/portfolio page structure).
export function useToggleSupportedForFollowup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patch(`/internal/flows/${id}/toggle-supported-for-followup`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["flows"] }),
  });
}

export async function getFlowInstanceCount(flowId: string): Promise<number> {
  const result = await get<{ count: number }>(`/internal/flows/${flowId}/instance-count`);
  return result.count;
}

export function useCloneFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CloneFlowRequest) =>
      post<CloneFlowResponse>(`/internal/flows/${id}/clone`, body satisfies CloneFlowRequest),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["flows"] });
      // QL-334 — the clone can bring a copy of the PDF template with it.
      void queryClient.invalidateQueries({ queryKey: ["pdf-templates"] });
      // A translated clone is queued: start polling its progress.
      void queryClient.invalidateQueries({ queryKey: CLONE_JOBS_KEY });
    },
  });
}

export function useDeleteFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/internal/flows/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["flows"] }),
  });
}

export function useFlow(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["flows", id],
    queryFn: () => get<any>(`/internal/flows/${id}`),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["flows", id] });
  return { flow: data ?? null, error, isLoading, mutate };
}

export function useFlowFields(flowId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["flows", flowId, "fields"],
    queryFn: () => get<FlowField[]>(`/internal/flows/${flowId}/fields`),
    enabled: !!flowId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["flows", flowId, "fields"] });
  return { fields: data ?? [], error, isLoading, mutate };
}

export function useFlowPage(flowId: string | null, pageId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["flows", flowId, "pages", pageId],
    queryFn: () => get<any>(`/internal/flows/${flowId}/pages/${pageId}`),
    enabled: !!flowId && !!pageId,
  });
  const queryClient = useQueryClient();
  // QL-195 — page-editor mutations (add/remove/duplicate components, layouts,
  // tabs) also change the pages list and component counts embedded in the
  // flow-detail response (["flows", flowId]), so invalidate the whole flow
  // prefix. That covers this page's own key (["flows", flowId, "pages", pageId])
  // and ["flows", flowId, "fields"] as well.
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["flows", flowId] });
  return { page: data ?? null, error, isLoading, mutate };
}
